import dayjs from 'dayjs';
import ExcelJS from 'exceljs';

/** 기본 대분류 라벨 */
export const SER_EVAL_DEFAULT_STEP_LABELS = [
  '경영자 안전보건경영활동',
  '위험성 평가',
  '안전보건교육',
  '근로자 참여',
  '중대재해 대응조치',
  '도급.용역.위탁 안전보건확보',
  '평가 및 개선',
  '안전문화 및 안전의식',
  '가점 사항',
];

/** 평가 라디오 값 → 표시문구 */
export const FormatSerEvalCheck = (check) => {
  if (check === 'Y') return '양호';
  if (check === 'N') return '미흡';
  return '해당없음';
};

/** Excel 시트명 금지문자 제거·길이 제한(31자) */
export const SanitizeExcelSheetName = (name, maxLen = 31) => {
  const cleaned = String(name ?? '')
    .replace(/[:\\/?*[\]]/g, '_')
    .trim();
  const base = cleaned || 'Sheet';
  return base.length > maxLen ? base.slice(0, maxLen) : base;
};

export const CreateDefaultSteps = (count = SER_EVAL_DEFAULT_STEP_LABELS.length) =>
  SER_EVAL_DEFAULT_STEP_LABELS.slice(0, count).map((label, i) => ({
    id: `cat-${i + 1}`,
    label,
  }));

const GetCheckpointShare = (indicator, checkpoints) => {
  const count = checkpoints?.length || 1;
  return Number(indicator?.val2 || 0) / count;
};

const RecomputeIndicatorScores = (arrayList, arrayList2) => {
  const updatedList = (arrayList || []).map((indicator) => {
    const checkpoints = (arrayList2 || []).filter(
      (c) => c.groupCd === indicator.code
    );
    const share = GetCheckpointShare(indicator, checkpoints);
    let totalPo = 0;
    checkpoints.forEach((cp) => {
      if (cp.check === 'Y') totalPo += share;
    });
    return {
      ...indicator,
      val3: totalPo.toFixed(2),
    };
  });
  const totalPoint = updatedList.reduce(
    (sum, row) => sum + Number(row.val3 || 0),
    0
  );
  return {
    arrayList: updatedList,
    totalPoint: Number(totalPoint.toFixed(2)),
  };
};

/** 상세 API 응답 → 평가 구조 (레거시/양식 미지정) */
export const MapDetailApiRowsToEvaluation = (data = []) => {
  if (!Array.isArray(data) || !data.length) return null;

  const groupedData = data.reduce((acc, item) => {
    const key = item.codeNm;
    if (!acc[key]) {
      acc[key] = {
        codeNm: key,
        val1: item.val1,
        val2: item.val2,
        code: item.groupCd,
        val3: 0,
      };
    }
    return acc;
  }, {});

  const arrayList = Object.values(groupedData);
  const arrayList2 = [];

  arrayList.forEach((indicator) => {
    const children = data.filter((e) => e.groupCd === indicator.code);
    const share = GetCheckpointShare(indicator, children);
    let totalPo = 0;

    children.forEach((item) => {
      const check = item.checkYn || 'N';
      if (check === 'Y') totalPo += share;
      arrayList2.push({
        code: item.seriousDisaster,
        groupCd: item.groupCd,
        codeNm: item.seriousDisasterNm,
        check,
        val1: item.val1,
      });
    });
    indicator.val3 = totalPo.toFixed(2);
  });

  const maxStep = Math.max(
    ...arrayList.map((row) => Number(row.val1) || 1),
    1
  );
  const steps = CreateDefaultSteps(maxStep);
  const totalPoint = arrayList.reduce(
    (sum, row) => sum + Number(row.val3 || 0),
    0
  );

  return {
    steps,
    arrayList,
    arrayList2,
    totalPoint: Number(totalPoint.toFixed(2)),
  };
};

/** 양식 구조 + 상세 평가값 병합 */
export const MergeTemplateWithDetailRows = (formData, detailRows = []) => {
  const steps =
    formData?.steps?.length > 0
      ? formData.steps
      : CreateDefaultSteps();
  const baseList = formData?.arrayList || [];
  const checkMap = new Map(
    detailRows.map((r) => [r.seriousDisaster, r.checkYn || '0'])
  );
  const arrayList2 = (formData?.arrayList2 || []).map((row) => ({
    ...row,
    check: checkMap.has(row.code) ? checkMap.get(row.code) : row.check || 'N',
  }));
  const { arrayList, totalPoint } = RecomputeIndicatorScores(baseList, arrayList2);

  return { steps, arrayList, arrayList2, totalPoint };
};

/**
 * 중대재해 평가 한 단계(val1)를 워크시트에 기록
 * @param {object} ctx - { arrayList, arrayList2, dateValue, totalPoint, custNm }
 */
export const WriteSeriousAccidentStepToWorksheet = (ws, stepVal, ctx) => {
  const { arrayList = [], arrayList2 = [], dateValue = {}, totalPoint = 0, custNm = '' } =
    ctx;
  const stepValStr = String(stepVal);
  ws.views = [{ state: 'frozen', ySplit: 3 }];

  const evaDateRaw = dateValue.evaDate;
  const evaDateOnly =
    evaDateRaw != null && String(evaDateRaw).trim() !== ''
      ? dayjs(evaDateRaw).isValid()
        ? dayjs(evaDateRaw).format('YYYY-MM-DD')
        : String(evaDateRaw).split(/[T ]/)[0].slice(0, 10)
      : '';

  ws.getRow(1).values = [
    '평가일자',
    evaDateOnly,
    '평가자',
    dateValue.evaName || '',
    '총 취득 점수',
    Math.round(Number(totalPoint) || 0),
  ];
  ws.getRow(2).values = ['사업장명', custNm || '-', '', '', '', ''];

  const headerRow = ws.getRow(3);
  headerRow.values = ['소계', '평가지표', '착안사항', '배점', '평가', '취득점수'];
  headerRow.font = { name: '맑은 고딕', size: 11, bold: true };
  headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
  for (let c = 1; c <= 6; c += 1) {
    const cell = headerRow.getCell(c);
    cell.border = {
      top: { style: 'thin', color: { argb: 'FF808080' } },
      left: { style: 'thin', color: { argb: 'FF808080' } },
      bottom: { style: 'thin', color: { argb: 'FF808080' } },
      right: { style: 'thin', color: { argb: 'FF808080' } },
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF3F7F8' },
    };
  }

  const stepRows = arrayList.filter((e) => String(e.val1) === stepValStr);
  let excelRow = 4;

  stepRows.forEach((_item, _index) => {
    const children = arrayList2.filter((e) => e.groupCd === _item.code);
    const subtotalLabel = `${_item.val1}-${_index + 1}`;
    const scoreDisplay = _item.val3 === '' || _item.val3 == null ? 0 : _item.val3;

    const writeDataRow = (child) => {
      const row = ws.getRow(excelRow);
      row.getCell(1).value = subtotalLabel;
      row.getCell(2).value = _item.codeNm || '';
      row.getCell(3).value = child?.codeNm || '';
      row.getCell(4).value = _item.val2 ?? '';
      row.getCell(5).value = FormatSerEvalCheck(child?.check);
      row.getCell(6).value = scoreDisplay;

      for (let c = 1; c <= 6; c += 1) {
        const cell = row.getCell(c);
        cell.font = { name: '맑은 고딕', size: 10 };
        cell.alignment = {
          vertical: 'middle',
          horizontal: c === 3 ? 'left' : 'center',
          wrapText: c === 3,
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF808080' } },
          left: { style: 'thin', color: { argb: 'FF808080' } },
          bottom: { style: 'thin', color: { argb: 'FF808080' } },
          right: { style: 'thin', color: { argb: 'FF808080' } },
        };
      }
      excelRow += 1;
    };

    if (!children.length) {
      writeDataRow(null);
    } else {
      children.forEach((child) => writeDataRow(child));
    }
  });

  if (!stepRows.length) {
    const emptyRow = ws.getRow(excelRow);
    emptyRow.getCell(1).value = '(해당 단계 평가 항목 없음)';
    emptyRow.getCell(1).font = { name: '맑은 고딕', size: 10, italic: true };
  }

  ws.columns = [
    { width: 10 },
    { width: 28 },
    { width: 48 },
    { width: 10 },
    { width: 12 },
    { width: 12 },
  ];
};

/** 전체 단계 워크북 생성 */
export const BuildSeriousAccidentWorkbook = (ctx) => {
  const { steps = [], arrayList = [] } = ctx;
  if (!arrayList.length) return null;

  const workbook = new ExcelJS.Workbook();

  steps.forEach((step, idx) => {
    const stepNo = idx + 1;
    const sheetName = SanitizeExcelSheetName(`${stepNo}_${step.label}`);
    const ws = workbook.addWorksheet(sheetName);
    WriteSeriousAccidentStepToWorksheet(ws, String(stepNo), ctx);
  });

  return workbook;
};

/** 워크북 파일 다운로드 */
export const DownloadSeriousAccidentWorkbook = async (workbook, fileName) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
};

/** 일괄 엑셀 파일명 */
export const BuildSeriousAccidentExcelFileName = ({ evaDate, evaName, suffix } = {}) => {
  const datePart = evaDate
    ? dayjs(evaDate).isValid()
      ? dayjs(evaDate).format('YYYYMMDD')
      : String(evaDate).slice(0, 10).replace(/-/g, '')
    : dayjs().format('YYYYMMDD');
  const namePart = evaName ? `_${String(evaName).slice(0, 10)}` : '';
  const tail = suffix || dayjs().format('HHmmss');
  return `중대재해처벌법_평가_전체단계_${datePart}${namePart}_${tail}.xlsx`;
};

/** 평가 컨텍스트 기준 일괄 엑셀 다운로드 */
export const DownloadSeriousAccidentExcelAll = async (ctx, fileName) => {
  const workbook = BuildSeriousAccidentWorkbook(ctx);
  if (!workbook) return false;
  await DownloadSeriousAccidentWorkbook(
    workbook,
    fileName ||
      BuildSeriousAccidentExcelFileName({
        evaDate: ctx?.dateValue?.evaDate,
        evaName: ctx?.dateValue?.evaName,
      })
  );
  return true;
};
